/**
 * ACMI费用录入页面
 * 参照旧系统设计，实现完整的费用录入流程
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { 
    Card, 
    Button, 
    Space, 
    Typography,
    InputNumber,
    Table,
    Form,
    Input,
    DatePicker,
    Row,
    Col,
    message,
    Checkbox,
    Divider,
    Tag,
    Tooltip,
    Modal
} from "antd";
import { 
    SearchOutlined,
    ReloadOutlined
} from "@ant-design/icons";
import { settlementApi } from '../../../api/settlementApi';
import { refreshSettlementBatchPending } from '../../../utils/settlementBatchPending';
import { rememberRows, rowsFromSelectedKeys } from '../utils/crossPageSelection';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import './AcmiEntryPage.css';

dayjs.extend(utc);

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const AcmiEntryPage = () => {
    const [form] = Form.useForm();
    
    // 单价输入
    const [unitPrice, setUnitPrice] = useState(null);
    
    // BH数据列表
    const [bhRecords, setBhRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    
    // 选中的记录
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const selectionCacheRef = useRef(new Map());
    
    // 分页
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    
    // 筛选条件
    const [filters, setFilters] = useState({
        flightNumber: '',
        departureAirport: '',
        arrivalAirport: '',
        dateRange: null,
        aircraftNumber: ''
    });
    
    // 提交状态
    const [submitting, setSubmitting] = useState(false);

    // 加载BH数据
    const loadBhRecords = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                page_size: pageSize,
                flight_number: filters.flightNumber || undefined,
                departure_airport: filters.departureAirport || undefined,
                arrival_airport: filters.arrivalAirport || undefined,
                start_date: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
                end_date: filters.dateRange?.[1]?.format('YYYY-MM-DD'),
                aircraft_number: filters.aircraftNumber || undefined,
            };

            console.log('查询BH数据参数:', params);
            const response = await settlementApi.acmi.getRecords(params);
            
            if (response.data && response.data.data) {
                const records = response.data.data.items || [];
                rememberRows(selectionCacheRef.current, records);
                setBhRecords(records);
                setTotalRecords(response.data.data.total || 0);
                console.log(`获取到 ${records.length} 条BH记录`);
            } else {
                setBhRecords([]);
                setTotalRecords(0);
            }
            
        } catch (error) {
            console.error('获取BH记录失败:', error);
            message.error('获取BH记录失败: ' + error.message);
            setBhRecords([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filters]);

    // 初始加载数据
    useEffect(() => {
        loadBhRecords();
    }, [loadBhRecords]);

    // 实时计算费用总计
    const calculatedData = useMemo(() => {
        const price = parseFloat(unitPrice) || 0;
        const totalBH = selectedRows.reduce((sum, record) => sum + (parseFloat(record.blockHours) || 0), 0);
        const totalCost = totalBH * price;
        
        // 计算每条记录的费用
        const recordsWithCost = selectedRows.map(record => ({
            ...record,
            calculatedCost: (parseFloat(record.blockHours) || 0) * price
        }));
        
        return {
            totalBH: totalBH.toFixed(2),
            unitPrice: price.toFixed(2),
            totalCost: totalCost.toFixed(2),
            recordsWithCost
        };
    }, [unitPrice, selectedRows]);

    // 表格列定义
    const columns = [
        {
            title: '航班号',
            dataIndex: 'flightNumber',
            key: 'flightNumber',
            width: 120,
            fixed: 'left',
            render: (text) => <Text strong>{text || '-'}</Text>
        },
        {
            title: '机号',
            dataIndex: 'aircraftNumber',
            key: 'aircraftNumber',
            width: 120,
            render: (text) => text || '-'
        },
        {
            title: '航段',
            dataIndex: 'route',
            key: 'route',
            width: 150,
            render: (text) => (
                <span style={{ 
                    backgroundColor: '#409eff', 
                    color: 'white', 
                    padding: '2px 8px', 
                    borderRadius: '12px',
                    fontSize: '12px'
                }}>
                    {text || '-'}
                </span>
            )
        },
        {
            title: 'ATD (起飞时间 UTC)',
            dataIndex: 'atd',
            key: 'atd',
            width: 200,
            render: (text, record) => {
                if (!text) return '-';
                try {
                    // 格式化为 YYYY-MM-DD HH:mm
                    const time = dayjs.utc(text).format('YYYY-MM-DD HH:mm');
                    
                    // 根据isActualTime字段区分显示
                    if (record.isActualTime) {
                        // 实际起飞时间 - 正常显示
                        return <Text>{time}</Text>;
                    } else {
                        // 计划起飞时间 - 灰色显示，带提示和标签
                        return (
                            <Tooltip title="实际起飞时间未记录，显示计划时间(STD)">
                                <span style={{ color: '#999' }}>
                                    {time} <Tag color="default" size="small">计划</Tag>
                                </span>
                            </Tooltip>
                        );
                    }
                } catch (e) {
                    return text;
                }
            }
        },
        {
            title: 'BH (小时)',
            dataIndex: 'blockHours',
            key: 'blockHours',
            width: 120,
            align: 'right',
            render: (value) => (
                <Text strong style={{ color: value > 0 ? '#52c41a' : '#f5222d' }}>
                    {value ? value.toFixed(2) : '0.00'}
                </Text>
            )
        },
        {
            title: '已有 ACMI',
            dataIndex: 'existingAmount',
            key: 'existingAmount',
            width: 120,
            align: 'right',
            render: (value, record) => {
                if (record.originalAmbiguous) return <Text type="danger">分不清原录入</Text>;
                if (value == null || value === '') return '—';
                return `$${Number(value).toFixed(2)}`;
            }
        }
    ];

    const applySelectedKeys = (keys) => {
        const unique = [...new Set(keys || [])];
        setSelectedRowKeys(unique);
        setSelectedRows(rowsFromSelectedKeys(selectionCacheRef.current, unique));
    };

    // 行选择配置
    const rowSelection = {
        selectedRowKeys,
        preserveSelectedRowKeys: true,
        onChange: (selectedKeys) => {
            applySelectedKeys(selectedKeys);
        },
        getCheckboxProps: (record) => ({
            disabled: !record.blockHours || record.blockHours <= 0,
            title: (!record.blockHours || record.blockHours <= 0) ? 'BH值必须大于0才能选择' : ''
        })
    };

    // 处理筛选条件变化
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    // 处理查询
    const handleSearch = () => {
        setCurrentPage(1);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        loadBhRecords();
    };

    // 处理重置
    const handleReset = () => {
        form.resetFields();
        setFilters({
            flightNumber: '',
            departureAirport: '',
            arrivalAirport: '',
            dateRange: null,
            aircraftNumber: ''
        });
        setCurrentPage(1);
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };

    // 处理刷新
    const handleRefresh = () => {
        loadBhRecords();
    };

    // 处理全选当前页
    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        if (checked) {
            const validRecords = bhRecords.filter(r => r.blockHours && r.blockHours > 0);
            rememberRows(selectionCacheRef.current, validRecords);
            const newKeys = validRecords.map(r => r.id);
            applySelectedKeys([...selectedRowKeys, ...newKeys]);
        } else {
            const currentPageKeys = new Set(bhRecords.map(r => r.id));
            applySelectedKeys(selectedRowKeys.filter(k => !currentPageKeys.has(k)));
        }
    };

    // 处理重置表单
    const handleResetForm = () => {
        setUnitPrice(null);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        message.success('已重置表单');
    };

    // 处理提交
    const handleSubmit = async () => {
        // 表单验证
        if (!unitPrice || unitPrice <= 0) {
            message.error('请输入大于0的单价');
            return;
        }
        
        if (selectedRows.length === 0) {
            message.error('请至少选择一个航段');
            return;
        }
        
        // 验证所有选中记录都有有效的BH值
        const hasInvalidBH = selectedRows.some(r => !r.blockHours || r.blockHours <= 0);
        if (hasInvalidBH) {
            message.error('所有选中的航段都必须有大于0的BH值');
            return;
        }

        const doSave = async () => {
            setSubmitting(true);
            try {
                const payload = {
                    legIds: selectedRows.map(r => r.flightOperationId || r.id),
                    costMetadata: {
                        costType: 'ACMI',
                        unitPrice: parseFloat(unitPrice),
                        totalBH: parseFloat(calculatedData.totalBH),
                        totalCost: parseFloat(calculatedData.totalCost)
                    }
                };

                const response = await settlementApi.acmi.saveCost(payload);
                refreshSettlementBatchPending(response.data?.data?.affected_batch_ids);
                message.success(`成功录入${selectedRows.length}个航段的ACMI费用，总计: $${calculatedData.totalCost}`);
                handleResetForm();
            } catch (error) {
                console.error('提交ACMI费用失败:', error);
                message.error('提交失败: ' + error.message);
            } finally {
                setSubmitting(false);
            }
        };

        const ambiguous = selectedRows.filter((row) => row.originalAmbiguous);
        if (ambiguous.length > 0) {
            const sample = ambiguous[0].flightNumber || "选中的航段";
            message.error(`${sample} 上有多笔有效的 ACMI，分不清哪一笔是原录入。这次没有改。`);
            return;
        }
        const withAdjustment = selectedRows.filter((row) => row.hasAdjustment);
        const withOriginal = selectedRows.filter((row) => row.existingAmount != null);
        if (withAdjustment.length > 0) {
            Modal.confirm({
                title: "这些航段已有调整",
                content: withOriginal.length > 0
                    ? "原录入会被覆盖，调整还在，实际会跟着原录入变。"
                    : "这些航段已有调整。这次会新记一笔原录入，调整还在，实际是两者相加。",
                okText: "确认",
                cancelText: "取消",
                onOk: doSave,
            });
            return;
        }
        if (withOriginal.length > 0) {
            Modal.confirm({
                title: "这些航段已有 ACMI，将按新单价覆盖",
                content: `${withOriginal.length} 个已选航段已有金额（例如 $${Number(withOriginal[0].existingAmount).toFixed(2)}）。确认后旧金额会被改掉，不会再记一笔。`,
                okText: "确认覆盖",
                cancelText: "取消",
                onOk: doSave,
            });
            return;
        }

        await doSave();
    };

    // 格式化金额
    const formatAmount = (amount) => {
        return Number(amount).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <div className="acmi-entry-page">
            {/* 页面头部 */}
            <Card className="page-header-card">
                <div>
                    <Title level={2} style={{ margin: 0 }}>
                        ACMI费用录入
                    </Title>
                    <Paragraph style={{ margin: '8px 0 0 0', color: '#666' }}>
                        费用类型: ACMI (Aircraft, Crew, Maintenance, Insurance)
                        <br />
                        计算公式: 总费用 = 单价/小时 × 飞行小时数(BH)
                    </Paragraph>
                </div>
            </Card>

            {/* 主要内容区域 - 左右分栏 */}
            <div className="content-layout">
                {/* 左侧面板 */}
                <div className="left-panel">
                    {/* 费用信息 */}
                    <Card title="费用信息" className="info-card">
                        <Form layout="horizontal" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                            <Form.Item 
                                label="ACMI单价(USD/小时)" 
                                required
                                help="请输入大于0的单价"
                            >
                                <InputNumber
                                    value={unitPrice}
                                    onChange={setUnitPrice}
                                    precision={2}
                                    min={0.01}
                                    max={999999.99}
                                    placeholder="请输入单价"
                                    style={{ width: '100%' }}
                                    size="large"
                                />
                            </Form.Item>
                        </Form>
                    </Card>

                    {/* 航段选择 */}
                    <Card 
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>航段选择</span>
                                <Space>
                                    <Text type="secondary">已选择</Text>
                                    <span style={{ 
                                        backgroundColor: '#52c41a', 
                                        color: 'white', 
                                        padding: '2px 12px', 
                                        borderRadius: '12px',
                                        fontWeight: 'bold'
                                    }}>
                                        {selectedRowKeys.length}
                                    </span>
                                    <Text type="secondary">个航段</Text>
                                </Space>
                            </div>
                        }
                        className="selection-card"
                    >
                        {/* 筛选表单 */}
                        <Form
                            form={form}
                            layout="inline"
                            style={{ marginBottom: 16, padding: 16, backgroundColor: '#fafafa', borderRadius: 4 }}
                        >
                            <Row gutter={[16, 16]} style={{ width: '100%' }}>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label="航班号" style={{ marginBottom: 0, width: '100%' }}>
                                        <Input
                                            placeholder="请输入"
                                            value={filters.flightNumber}
                                            onChange={(e) => handleFilterChange('flightNumber', e.target.value)}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label="始发站" style={{ marginBottom: 0, width: '100%' }}>
                                        <Input
                                            placeholder="机场代码"
                                            value={filters.departureAirport}
                                            onChange={(e) => handleFilterChange('departureAirport', e.target.value)}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label="目的站" style={{ marginBottom: 0, width: '100%' }}>
                                        <Input
                                            placeholder="机场代码"
                                            value={filters.arrivalAirport}
                                            onChange={(e) => handleFilterChange('arrivalAirport', e.target.value)}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label="飞机号" style={{ marginBottom: 0, width: '100%' }}>
                                        <Input
                                            placeholder="请输入"
                                            value={filters.aircraftNumber}
                                            onChange={(e) => handleFilterChange('aircraftNumber', e.target.value)}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="日期范围" style={{ marginBottom: 0, width: '100%' }}>
                                        <RangePicker
                                            style={{ width: '100%' }}
                                            value={filters.dateRange}
                                            onChange={(dates) => handleFilterChange('dateRange', dates)}
                                            format="YYYY-MM-DD"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Space>
                                        <Button 
                                            type="primary" 
                                            icon={<SearchOutlined />} 
                                            onClick={handleSearch}
                                            loading={loading}
                                        >
                                            查询
                                        </Button>
                                        <Button onClick={handleReset}>重置</Button>
                                        <Button icon={<ReloadOutlined />} onClick={handleRefresh}>刷新</Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>

                        {/* 全选操作 */}
                        <div style={{ marginBottom: 12, padding: '8px 12px', backgroundColor: '#f0f9ff', borderRadius: 4 }}>
                            <Checkbox 
                                onChange={handleSelectAll}
                                checked={bhRecords.length > 0 && bhRecords.filter(r => r.blockHours > 0).every(r => selectedRowKeys.includes(r.id))}
                            >
                                全选当前页（当前页 {bhRecords.filter(r => r.blockHours > 0).length} 个可选航段）
                            </Checkbox>
                        </div>

                        {/* 航段列表 */}
                        <Table
                            rowSelection={rowSelection}
                            columns={columns}
                            dataSource={bhRecords}
                            rowKey="id"
                            size="small"
                            bordered
                            loading={loading}
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                total: totalRecords,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total) => `共 ${total} 条记录`,
                                pageSizeOptions: ['25', '50', '100'],
                                onChange: (page, size) => {
                                    setCurrentPage(page);
                                    setPageSize(size);
                                }
                            }}
                            scroll={{ x: 900 }}
                        />
                    </Card>
                </div>

                {/* 右侧面板 */}
                <div className="right-panel">
                    {/* 费用预览 */}
                    <Card title="费用预览" className="preview-card">
                        {selectedRows.length === 0 ? (
                            <div className="empty-preview">
                                <Text type="secondary">请先输入单价并选择航段</Text>
                            </div>
                        ) : (
                            <>
                                <div className="preview-list">
                                    {calculatedData.recordsWithCost.map((record) => (
                                        <div key={record.id} className="preview-item">
                                            <div className="item-header">
                                                <Text strong>{record.route || '-'}</Text>
                                                <Text strong style={{ color: '#52c41a', fontSize: 16 }}>
                                                    ${formatAmount(record.calculatedCost)}
                                                </Text>
                                            </div>
                                            <div className="item-details">
                                                <Text type="secondary">
                                                    {record.flightNumber} | BH: {record.blockHours?.toFixed(2)} 小时
                                                </Text>
                                                <Text type="secondary">
                                                    单价: ${formatAmount(unitPrice || 0)}/小时
                                                </Text>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                <Divider />
                                
                                <div className="preview-total">
                                    <div className="total-row">
                                        <Text strong>费用总计 (USD):</Text>
                                        <Text strong style={{ fontSize: 20, color: '#52c41a' }}>
                                            ${formatAmount(calculatedData.totalCost)}
                                        </Text>
                                    </div>
                                </div>
                            </>
                        )}
                    </Card>

                    {/* 操作按钮 */}
                    <Card className="action-card">
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            <Button 
                                type="primary" 
                                size="large"
                                block
                                disabled={!unitPrice || selectedRows.length === 0 || submitting}
                                loading={submitting}
                                onClick={handleSubmit}
                            >
                                确认录入
                            </Button>
                            <Button 
                                size="large"
                                block
                                onClick={handleResetForm}
                            >
                                重置
                            </Button>
                        </Space>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default AcmiEntryPage;
